import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError, api } from '../api/client';
import type { components } from '../api/schema.gen';

type Me = components['schemas']['Me'];

export type ProfileUpdate = {
  display_name?: string | null;
  username?: string;
  date_of_birth?: string | null;
  phone_e164?: string | null;
  avatar_data_url?: string | null;
};

interface SessionState {
  me: Me | null;
  /** True once `/me` has resolved (successfully or not) at least once. */
  ready: boolean;
  /**
   * Whether this account belongs to an organization. Billing, keys and usage
   * are all organized under one — an account with none is mid-onboarding,
   * not broken, and every org-scoped page renders that as an empty state
   * rather than an error (see OnboardingNotice).
   */
  hasOrg: boolean;
  balance: string | null;
  balanceUpdatedAt: string | null;
  balanceError: ApiError | null;
  /**
   * Whether the org has spent through its credit, from GET /balance/status —
   * open to every role, unlike `balance` above. This is how a plain member
   * (who never sees `balance`, LLMR-55) still learns "out of credit, ask an
   * admin" without ever being told the amount. Null until the first fetch
   * resolves.
   */
  depleted: boolean | null;
  /**
   * Whether the org has ever received a positive ledger credit, from the
   * same GET /balance/status response. Distinguishes a brand-new wallet
   * (depleted + not everFunded) from one that spent through previously
   * held credit (depleted + everFunded). Null until the first fetch
   * resolves — same lifecycle as `depleted`.
   */
  everFunded: boolean | null;
  refreshBalance: () => Promise<void>;
  /**
   * Owner/admin, same rule as member-api's canManageMembers (LLMR-55): a
   * plain member never sees the org balance or creates a top-up, only their
   * own spend. Read from the ACTIVE org's role (LLMR-54) — `me.organizations`
   * can hold more than one now, so this is `.find(active_org_id)`, not `[0]`.
   */
  canManageBilling: boolean;
  /**
   * Whether the ACTIVE org (not necessarily every org this account belongs
   * to) is a b2b organization — `me.account_type` is already derived from
   * exactly that org's type + the caller's role in it
   * (`auth.EffectiveAccountType`, computed server-side on every /me read).
   * Exists so copy that talks about "your organization" doesn't say that to
   * an individual account, which never chose to be a company — see
   * LLMR-team-member-limit's wording pass. `false` while `me` hasn't
   * loaded yet, same as every other me-derived flag here.
   */
  isB2BOrg: boolean;
  /**
   * Switch which org the session resolves to (LLMR-54), then reload the
   * page. A reload, not an in-place state patch: balance, depleted, and
   * every role-gated bit of UI were all fetched for the OLD org, and
   * re-deriving them correctly in place is more risk than this rare action
   * is worth. Throws on failure (e.g. a stale org_id no longer valid) —
   * the caller renders that.
   */
  switchOrg: (orgId: string) => Promise<void>;
  /**
   * Same-origin URL for the stored avatar, or null when `has_avatar` is false.
   * Cache-busted after each successful profile save so the topbar refreshes.
   */
  avatarUrl: string | null;
  /** Persist profile fields via PATCH /me and refresh in-memory `me`. */
  updateProfile: (patch: ProfileUpdate) => Promise<Me>;
  /**
   * Re-fetch `/me` without writing anything.
   *
   * For the changes this app makes to a profile through some route other than
   * PATCH /me — today that is phone verification, which sets `phone_country`
   * server-side and so decides whether SMS appears as a second factor. Without
   * this the page would keep rendering a stale "not verified" until a reload.
   */
  refreshMe: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

function avatarSrc(me: Me | null, bust: number): string | null {
  if (!me?.has_avatar) return null;
  return bust > 0 ? `/api/me/avatar?v=${bust}` : '/api/me/avatar';
}

/**
 * Fetches `/me` and `/balance` once, high in the tree, and shares them.
 *
 * The balance is still read fresh from the server on every mount that needs
 * it to be current — this context exists to avoid three components each
 * issuing their own `/me` call on first paint, not to cache a stale balance
 * across the session. `refreshBalance` lets a page ask for a current number
 * when it actually matters (e.g. after a Playground turn, or returning from
 * a top-up). The displayed figure is always whatever GET /balance last said;
 * never derived from a usage stream.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);
  const [balance, setBalance] = useState<string | null>(null);
  const [balanceUpdatedAt, setBalanceUpdatedAt] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<ApiError | null>(null);
  const [depleted, setDepleted] = useState<boolean | null>(null);
  const [everFunded, setEverFunded] = useState<boolean | null>(null);
  const [avatarBust, setAvatarBust] = useState(0);

  const refreshBalance = useCallback(async () => {
    try {
      const b = await api.balance();
      setBalance(b.balance.amount);
      setBalanceUpdatedAt(b.updated_at);
      setBalanceError(null);
    } catch (e) {
      // Not authenticated is handled above this provider; anything else
      // (chiefly 403 — no organization yet, or a plain member — LLMR-55) is
      // the caller's to render.
      if (e instanceof ApiError) setBalanceError(e);
    }
    try {
      const s = await api.balanceStatus();
      setDepleted(s.depleted);
      setEverFunded(s.ever_funded);
    } catch {
      // Same "no org yet" cases as above swallow here too — a page that
      // cares reads `hasOrg`/`ready` first, same as it does for `balance`.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const m = await api.me();
        if (!cancelled) setMe(m);
      } catch {
        // Swallowed deliberately: App already treats a failed /me as
        // "signed out" and won't render anything that uses this context.
      }
      await refreshBalance();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshBalance]);

  const updateProfile = useCallback(async (patch: ProfileUpdate) => {
    const next = await api.updateProfile(patch);
    setMe(next);
    if (patch.avatar_data_url !== undefined) {
      setAvatarBust(Date.now());
    }
    return next;
  }, []);

  const refreshMe = useCallback(async () => {
    // Deliberately not swallowing a failure into `me = null`: a transient
    // error here should leave the last known profile on screen rather than
    // blank the topbar and every gated section that reads it.
    const next = await api.me();
    setMe(next);
  }, []);

  const hasOrg = (me?.organizations.length ?? 0) > 0;
  const avatarUrl = avatarSrc(me, avatarBust);
  const myRole = me?.organizations.find((o) => o.org_id === me.active_org_id)?.role;
  const canManageBilling = myRole === 'owner' || myRole === 'admin';
  const isB2BOrg = me?.account_type === 'b2b_admin' || me?.account_type === 'b2b_member';

  const switchOrg = useCallback(async (orgId: string) => {
    await api.setActiveOrg(orgId);
    window.location.reload();
  }, []);

  const value = useMemo(
    () => ({
      me,
      ready,
      hasOrg,
      balance,
      balanceUpdatedAt,
      balanceError,
      depleted,
      everFunded,
      refreshBalance,
      canManageBilling,
      isB2BOrg,
      switchOrg,
      avatarUrl,
      updateProfile,
      refreshMe,
    }),
    [
      me,
      ready,
      hasOrg,
      balance,
      balanceUpdatedAt,
      balanceError,
      depleted,
      everFunded,
      refreshBalance,
      canManageBilling,
      isB2BOrg,
      switchOrg,
      avatarUrl,
      updateProfile,
      refreshMe,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
